import { ValidatorFn } from '@angular/forms';
import { Product, ProductQuantityUnit, PurchaseProductAction } from '../../core/api/api.entities';
import { ProductWithBarcode } from '../../shared/models/product-with-barcode.model';
import { roundNumber } from '../../shared/utils/common.utils';
import { optionalDateToISOString } from '../../shared/utils/date.utils';
import { requiredNotNull } from '../form-control-options.constants';
import { ShoppingItemFormControls } from '../form-controls/shopping-item.form-controls';
import { ShoppingItemFormOptions } from '../form-options/shopping-item.form-options';
import { BaseForm } from './base.form';

export class ShoppingItemForm extends BaseForm<ShoppingItemFormControls> {
  formGroup = this.createFormGroup(ShoppingItemFormControls);
  override options = new ShoppingItemFormOptions();
  _product?: ProductWithBarcode;
  shoppingSessionId?: number;
  storeId?: number;

  get amount(): number {
    return this.controls.amount.value || 1;
  }

  get conversionFactor(): number {
    return this.product?.conversionPurchaseToStockFactor ?? 1;
  }

  get newStock(): string {
    const currentStock = this.product?.activeStockAmount ?? 0;
    const newAmount = currentStock + this.amount * this.conversionFactor;

    const quantityUnit =
      newAmount === 1 ? this.product?.quantityUnitStock.name : this.product?.quantityUnitStock.namePlural;

    return `${currentStock} → ${newAmount} ${quantityUnit}`;
  }

  get pantIncluded(): boolean {
    return this.controls.pantIncluded.value || false;
  }

  get product(): ProductWithBarcode | undefined {
    return this._product;
  }

  set product(product: ProductWithBarcode | undefined) {
    this._product = product;

    if (product) {
      this.controls.amount.setValue(product.barcode?.amount || 1);
      this.controls.purchaseQuPant.setValue(product.pant || null);
      this.setInitialBestBeforeDate(product);
      this.setInitialPrice(product);
    }

    this.setChangeListeners();
  }

  get purchaseQuantityUnit(): ProductQuantityUnit | undefined {
    return this.product?.quantityUnitPurchase;
  }

  get purchaseQuantityUnitName(): string {
    if (this.purchaseQuantityUnit) {
      return this.amount === 1 ? this.purchaseQuantityUnit.name : this.purchaseQuantityUnit.namePlural;
    } else return '';
  }

  get purchaseQuantityUnitPant(): number | undefined {
    return this.controls.purchaseQuPant.value ?? undefined;
  }

  get purchaseQuantityUnitPrice(): number | undefined {
    return this.controls.purchaseQuPrice.value ?? undefined;
  }

  get purchaseQuantityUnitPriceWithoutPant(): number | undefined {
    if (this.purchaseQuantityUnitPrice) {
      if (this.pantIncluded) {
        return this.purchaseQuantityUnitPrice - this.purchaseQuantityUnitPant!;
      } else return this.purchaseQuantityUnitPrice;
    } else return undefined;
  }

  get totalDiscount(): number | undefined {
    return this.controls.totalDiscount.value || undefined;
  }

  get totalPant(): number | undefined {
    return this.unitPant !== undefined ? this.unitPant * this.amount : undefined;
  }

  get totalPrice(): number | undefined {
    return this.unitPrice !== undefined ? this.unitPrice * this.amount : undefined;
  }

  get totalPriceWithoutDiscount(): number | undefined {
    return this.unitPriceWithoutDiscount !== undefined ? this.unitPriceWithoutDiscount * this.amount : undefined;
  }

  get unitDiscount(): number | undefined {
    return this.totalDiscount !== undefined ? this.totalDiscount / this.amount : undefined;
  }

  get unitPant(): number | undefined {
    return this.purchaseQuantityUnitPant !== undefined
      ? this.purchaseQuantityUnitPant / this.conversionFactor
      : undefined;
  }

  get unitPrice(): number | undefined {
    return this.unitPriceWithoutDiscount !== undefined
      ? this.unitPriceWithoutDiscount - (this.unitDiscount ?? 0)
      : undefined;
  }

  get unitPriceWithoutDiscount(): number | undefined {
    return this.purchaseQuantityUnitPriceWithoutPant !== undefined
      ? this.purchaseQuantityUnitPriceWithoutPant / this.conversionFactor
      : undefined;
  }

  disable(): void {
    this.controls.amount.disable();
    this.controls.bestBeforeDate.disable();
    this.controls.neverOverdue.disable();
    this.controls.pantIncluded.disable();
    this.controls.purchaseQuPant.disable();
    this.controls.purchaseQuPrice.disable();
    this.controls.totalDiscount.disable();
    this.controls.totalPrice.disable();
  }

  enable(): void {
    this.controls.amount.enable();
    this.controls.neverOverdue.enable();
    this.controls.pantIncluded.enable();
    this.controls.purchaseQuPant.enable();
    this.controls.purchaseQuPrice.enable();
    this.controls.totalDiscount.enable();
    this.controls.totalPrice.enable();

    if (this.controls.neverOverdue.value === false) {
      this.controls.bestBeforeDate.enable();
    }
  }

  getProductAction(): PurchaseProductAction {
    if (!this.formGroup.valid) {
      throw new Error('Form is invalid');
    }

    const controls = this.controls;

    return {
      amount: this.amount * this.conversionFactor,
      discount: this.unitDiscount,
      expiresAt: optionalDateToISOString(controls.bestBeforeDate.value),
      pant: this.unitPant,
      price: this.unitPrice,
    };
  }

  private setChangeListeners(): void {
    this.controls.neverOverdue.valueChanges.subscribe(value => {
      if (this.controls.neverOverdue.disabled) {
        return;
      }

      if (value === false) {
        const bestBeforeDate = this.getDefaultBestBeforeDate(this.product);
        this.controls.bestBeforeDate.setValidators(requiredNotNull.validators as ValidatorFn[]);
        this.controls.bestBeforeDate.setValue(bestBeforeDate || new Date());
        this.controls.bestBeforeDate.enable();
      } else {
        this.controls.bestBeforeDate.clearValidators();
        this.controls.bestBeforeDate.setValue(null);
        this.controls.bestBeforeDate.disable();
      }
    });

    this.controls.purchaseQuPrice.valueChanges.subscribe(value => {
      if (this.controls.purchaseQuPrice.disabled) return;

      if (value) {
        const price = value * this.conversionFactor;
        const totalPrice = roundNumber(price * this.amount, 4);
        if (this.controls.totalPrice.value !== totalPrice) {
          this.controls.totalPrice.setValue(totalPrice, { emitEvent: false });
        }
      }
    });

    this.controls.totalPrice.valueChanges.subscribe(value => {
      if (this.controls.totalPrice.disabled) return;

      if (value) {
        const price = value / this.amount;
        const purchaseQuPrice = roundNumber(price / this.conversionFactor, 4);
        if (this.controls.purchaseQuPrice.value !== purchaseQuPrice) {
          this.controls.purchaseQuPrice.setValue(purchaseQuPrice, { emitEvent: false });
        }
      }
    });

    this.controls.amount.valueChanges.subscribe(value => {
      if (this.controls.amount.disabled) return;

      const price = this.controls.purchaseQuPrice.value
        ? roundNumber(this.controls.purchaseQuPrice.value * this.conversionFactor, 4)
        : null;
      this.controls.totalPrice.setValue(price ? roundNumber(price * value, 4) : null);
    });
  }

  private setInitialBestBeforeDate(product: Product): void {
    const bestBeforeDate = this.getDefaultBestBeforeDate(product);
    if (bestBeforeDate) {
      this.controls.bestBeforeDate.setValue(bestBeforeDate);
      this.controls.neverOverdue.setValue(false);
      this.controls.bestBeforeDate.enable();
    } else {
      this.controls.neverOverdue.setValue(true);
      this.controls.bestBeforeDate.setValidators(requiredNotNull.validators as ValidatorFn[]);
      this.controls.bestBeforeDate.setValue(null);
      this.controls.bestBeforeDate.disable();
    }
  }

  private setInitialPrice({ prices }: Product): void {
    if (prices) {
      const price = prices.current.find(price => price.storeId === this.storeId)?.price;
      if (price) {
        this.controls.purchaseQuPrice.setValue(price * this.conversionFactor);
        this.controls.totalPrice.setValue(price * this.amount * this.conversionFactor);
      }
    }
  }

  private getDefaultBestBeforeDate(product: Product | undefined): Date | null {
    const { defaultBestBeforeDays, nextDueAt } = product || {};

    if (defaultBestBeforeDays) {
      const nextDueDate = new Date();
      nextDueDate.setDate(nextDueDate.getDate() + defaultBestBeforeDays);
      return nextDueDate;
    }

    if (nextDueAt) {
      return new Date(nextDueAt);
    } else return null;
  }
}
